/**
 * Estate Flow - Property Management Platform
 * Copyright © 2025 Sebastian Alliance Group LLC. All Rights Reserved.
 * 
 * PROPRIETARY AND CONFIDENTIAL
 * 
 * This software and associated documentation files (the "Software") are the 
 * proprietary property of Sebastian Alliance Group LLC and are protected by 
 * copyright, trademark, and other intellectual property laws.
 * 
 * Unauthorized copying, modification, distribution, or use of this Software,
 * via any medium, is strictly prohibited without express written permission
 * from Sebastian Alliance Group LLC.
 * 
 * For licensing inquiries: legal@sebastianalliancegroup.com
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import VendorManager from './VendorManager';
import VendorInvoiceReview from './VendorInvoiceReview';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { 
  Building2, Users, DollarSign, TrendingUp, Wrench, 
  UserPlus, PlusCircle, Bell, FileText, Settings, LogOut,
  Calendar, AlertCircle, CheckCircle, Clock, Edit, Trash2, Eye,
  ChevronDown, ChevronUp, Receipt, Calculator, Activity, HelpCircle, Download, Mail
} from 'lucide-react';
import ScheduleMaintenanceModal from './hoa/ScheduleMaintenanceModal';
import RecordDuesModal from './hoa/RecordDuesModal';
import PLReports from './PLReports';
import FinancialAnalytics from './FinancialAnalytics';
import HOAMeetings from './HOAMeetings';
import HOAAssessments from './HOAAssessments';
import BoardReportEmailModal from './BoardReportEmailModal';
import StaffManagement from './hoa/StaffManagement';
import useAccessControl from '../hooks/useAccessControl';

const HOAAdminDashboard = () => {
  const navigate = useNavigate();
  const { can } = useAccessControl();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'overview';
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [maintenanceRequests, setMaintenanceRequests] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [activeTab, setActiveTab] = useState(initialTab);
  const isPlatformOwner = localStorage.getItem('user_role') === 'platform_super_admin';
  const [orgMemberships, setOrgMemberships] = useState([]);
  const [orgSwitcherOpen, setOrgSwitcherOpen] = useState(false);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab }, { replace: true });
  };
  const [showAddOwner, setShowAddOwner] = useState(false);
  const [showRecordDues, setShowRecordDues] = useState(false);
  const [showAnnouncement, setShowAnnouncement] = useState(false);
  const [showMaintenance, setShowMaintenance] = useState(false);
  const [invitationCode, setInvitationCode] = useState('');
  const [owners, setOwners] = useState([]);
  const [financialSummary, setFinancialSummary] = useState(null);
  const [settings, setSettings] = useState(null);
  const [duesRecords, setDuesRecords] = useState([]);
  const [expandedOwner, setExpandedOwner] = useState(null);

  const generateInvitationCode = () => {
    return Math.random().toString(36).substring(2, 10).toUpperCase();
  };

  const handleAddOwner = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData(e.target);
      const ownerData = {
        first_name: formData.get('first_name'),
        last_name: formData.get('last_name'),
        email: formData.get('email'),
        phone: formData.get('phone'),
        unit_number: formData.get('unit_number'),
        expires_in_days: 7
      };
      
      const response = await api.post('/hoa/owners/invite', ownerData);
      
      setInvitationCode(response.data.invite_code);
      if (response.data.email_sent) {
        toast.success(`Invitation sent to ${response.data.email}! Code: ${response.data.invite_code}`);
      } else if (response.data.email) {
        toast.warning(`Invitation created but email delivery failed. Share the code manually: ${response.data.invite_code}`);
      } else {
        toast.success(`Invitation created! Code: ${response.data.invite_code}`);
      }
    } catch (error) {
      console.error('Error creating invitation:', error);
      toast.error(error.response?.data?.detail || 'Failed to create invitation');
    }
  };

  useEffect(() => {
    fetchDashboardData();
    fetchMaintenanceRequests();
    fetchVendors();
    fetchOwners();
    fetchMemberships();
  }, []);

  const fetchMemberships = async () => {
    try {
      const { data } = await api.get('/auth/memberships');
      setOrgMemberships(data.memberships || []);
    } catch (e) { /* single-org user */ }
  };

  const switchOrganization = async (membership) => {
    try {
      const { data } = await api.post('/auth/select-membership', { membership_id: membership.id });
      if (data.access_status === 'locked') {
        toast.error(data.message || 'This organization is currently locked.');
        return;
      }
      if (data.access_token) {
        localStorage.setItem('access_token', data.access_token);
      }
      if (data.role) localStorage.setItem('user_role', data.role);
      if (data.org_id) localStorage.setItem('active_org_id', data.org_id);
      if (data.membership_id) localStorage.setItem('active_membership_id', data.membership_id);
      setOrgSwitcherOpen(false);
      // Determine correct portal based on dashboard_url or org type
      const targetDashboard = data.dashboard_url || '/hoa-admin/dashboard';
      const role = data.role || '';
      if (role === 'landlord' || role === 'property_manager' || role === 'hoa_owner') {
        window.location.replace(targetDashboard);
      } else {
        // Same portal type — just refetch all data
        setLoading(true);
        await Promise.all([fetchDashboardData(), fetchMaintenanceRequests(), fetchVendors(), fetchOwners()]);
        setLoading(false);
        toast.success(`Switched to ${data.organization_name}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to switch organization');
    }
  };

  useEffect(() => {
    if (activeTab === 'financial') {
      fetchFinancialSummary();
    } else if (activeTab === 'settings') {
      fetchSettings();
    }
  }, [activeTab]);

  useEffect(() => {
    const refreshFinancials = () => fetchFinancialSummary();
    window.addEventListener('estateflow:financials-updated', refreshFinancials);
    return () => window.removeEventListener('estateflow:financials-updated', refreshFinancials);
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await api.get('/hoa/dashboard');
      setDashboardData(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Failed to load dashboard');
      setLoading(false);
    }
  };

  const fetchMaintenanceRequests = async () => {
    try {
      const response = await api.get('/hoa/maintenance-requests');
      setMaintenanceRequests(response.data.maintenance_requests || []);
    } catch (error) {
      console.error('Error fetching maintenance requests:', error);
    }
  };

  const fetchOwners = async () => {
    try {
      const response = await api.get('/hoa/owners');
      setOwners(response.data.owners || []);
      
      // Also fetch dues records to show payment status
      const duesResponse = await api.get('/hoa/dues');
      setDuesRecords(duesResponse.data.dues || []);
    } catch (error) {
      console.error('Error fetching owners:', error);
    }
  };

  const getOwnerPaymentStatus = (ownerId) => {
    // Get current month dues for this owner
    const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM
    const currentDues = duesRecords.find(d => 
      d.owner_id === ownerId && 
      d.due_date?.startsWith(currentMonth)
    );
    
    if (!currentDues) return { status: 'N/A', color: 'bg-gray-100 text-gray-800' };
    
    if (currentDues.status === 'paid') {
      return { status: 'Paid', color: 'bg-green-100 text-green-800', amount: currentDues.amount };
    } else if (currentDues.status === 'unpaid') {
      return { status: 'Unpaid', color: 'bg-red-100 text-red-800', amount: currentDues.amount };
    } else if (currentDues.status === 'late') {
      return { status: 'Late', color: 'bg-orange-100 text-orange-800', amount: currentDues.amount };
    }
    return { status: currentDues.status, color: 'bg-gray-100 text-gray-800', amount: currentDues.amount };
  };

  const getOwnerPaymentHistory = (ownerId) => {
    // Get all payment records for this owner, sorted by date (newest first)
    return duesRecords
      .filter(d => d.owner_id === ownerId)
      .sort((a, b) => new Date(b.due_date) - new Date(a.due_date));
  };

  const calculateOwnerTotals = (ownerId) => {
    const history = getOwnerPaymentHistory(ownerId);
    const totalPaid = history.filter(d => d.status === 'paid').reduce((sum, d) => sum + d.amount, 0);
    const totalUnpaid = history.filter(d => d.status === 'unpaid').reduce((sum, d) => sum + d.amount, 0);
    const totalLate = history.filter(d => d.status === 'late').reduce((sum, d) => sum + (d.amount + d.late_fee), 0);
    
    return {
      totalPaid,
      totalUnpaid,
      totalLate,
      recordCount: history.length,
      paidCount: history.filter(d => d.status === 'paid').length,
      unpaidCount: history.filter(d => d.status === 'unpaid').length
    };
  };

  const fetchFinancialSummary = async () => {
    try {
      const response = await api.get('/hoa/finance/summary');
      setFinancialSummary(response.data);
    } catch (error) {
      console.error('Error fetching financial summary:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await api.get('/hoa/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await api.get('/hoa/vendors');
      setVendors(response.data.vendors || []);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user_role');
    localStorage.removeItem('user_id');
    navigate('/property-manager/login');
  };

  const [downloadingReport, setDownloadingReport] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);

  const handleDownloadBoardReport = async () => {
    try {
      setDownloadingReport(true);
      const response = await api.get('/hoa/board-report/pdf', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Board_Report_${new Date().toISOString().slice(0, 10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Board report downloaded!');
    } catch (error) {
      console.error('Error downloading board report:', error);
      toast.error('Failed to download board report');
    } finally {
      setDownloadingReport(false);
    }
  };

  const handleDownloadBoardSubmission = async () => {
    try {
      setDownloadingReport(true);
      const response = await api.get('/hoa/board-submission/pdf', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `RPC_Board_Submission_${new Date().toISOString().slice(0, 10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Board submission document downloaded!');
    } catch (error) {
      console.error('Error downloading board submission:', error);
      toast.error('Failed to download board submission');
    } finally {
      setDownloadingReport(false);
    }
  };

  const handleMaintenanceStatusUpdate = async (requestId, newStatus) => {
    try {
      await api.patch(`/hoa/maintenance/${requestId}/status`, { status: newStatus });
      toast.success(`Maintenance request ${newStatus}`);
      fetchMaintenanceRequests();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleUpdateSettings = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData(e.target);
      const settingsData = {
        default_dues: parseFloat(formData.get('default_dues')),
        late_fee_rate: parseFloat(formData.get('late_fee_rate')),
        maintenance_threshold: parseFloat(formData.get('maintenance_threshold')),
        notify_email: formData.get('notify_email') === 'on',
        notify_sms: formData.get('notify_sms') === 'on',
        due_day: parseInt(formData.get('due_day')),
        grace_period_days: parseInt(formData.get('grace_period_days'))
      };
      
      await api.post('/hoa/settings', settingsData);
      toast.success('Settings updated successfully!');
      fetchSettings();
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-100 text-blue-800',
      assigned: 'bg-yellow-100 text-yellow-800',
      in_progress: 'bg-purple-100 text-purple-800',
      completed: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">HOA Admin Dashboard</h1>
              {orgMemberships.length > 1 ? (
                <div className="relative" data-testid="org-switcher">
                  <button
                    onClick={() => setOrgSwitcherOpen(!orgSwitcherOpen)}
                    className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 transition-colors group"
                    data-testid="org-switcher-trigger"
                  >
                    <span className="font-medium">{dashboardData?.organization?.name || 'Organization'}</span>
                    <svg className={`h-4 w-4 transition-transform ${orgSwitcherOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                  </button>
                  {orgSwitcherOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setOrgSwitcherOpen(false)} />
                      <div className="absolute left-0 top-full mt-1 w-72 bg-white rounded-lg shadow-lg border z-50 py-1" data-testid="org-switcher-dropdown">
                        {orgMemberships.filter(m => m.access_status !== 'revoked').map(m => {
                          const isActive = m.org_id === (dashboardData?.organization?.id || localStorage.getItem('active_org_id'));
                          const isLocked = m.access_status === 'locked';
                          return (
                            <button
                              key={m.id || m.org_id}
                              onClick={() => !isActive && !isLocked && switchOrganization(m)}
                              disabled={isLocked}
                              className={`w-full text-left px-4 py-2.5 text-sm flex items-center justify-between hover:bg-gray-50 transition-colors ${isActive ? 'bg-emerald-50' : ''} ${isLocked ? 'opacity-50 cursor-not-allowed' : ''}`}
                              data-testid={`org-switch-${m.org_id}`}
                            >
                              <div>
                                <p className={`font-medium ${isActive ? 'text-emerald-700' : 'text-gray-900'}`}>{m.organization_name}</p>
                                <p className="text-xs text-gray-500">{m.role_label || m.staff_role_name || m.staff_role || m.portal_role || m.role}</p>
                                {isLocked && <p className="text-xs text-red-500">{m.access_reason || 'Locked'}</p>}
                              </div>
                              {isActive && <span className="text-emerald-600 text-xs font-medium">Active</span>}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-600">{dashboardData?.organization?.name || 'Community Association'}</p>
              )}
            </div>
            <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
              <Button
                onClick={handleDownloadBoardReport}
                disabled={downloadingReport}
                size="sm"
                data-testid="download-board-report-btn"
                className="bg-primary text-white text-xs sm:text-sm"
              >
                <Download className="mr-1 sm:mr-2 h-4 w-4" />
                {downloadingReport ? 'Generating...' : 'Board Report'}
              </Button>
              {isPlatformOwner && (
                <Button
                  onClick={handleDownloadBoardSubmission}
                  disabled={downloadingReport}
                  size="sm"
                  data-testid="download-board-submission-btn"
                  className="bg-slate-700 hover:bg-slate-800 text-white text-xs sm:text-sm"
                >
                  <FileText className="mr-1 sm:mr-2 h-4 w-4" />
                  Board Submission
                </Button>
              )}
              <Button
                onClick={() => setShowEmailModal(true)}
                size="sm"
                variant="outline"
                data-testid="send-board-report-email-btn"
                className="text-xs sm:text-sm border-slate-300"
              >
                <Mail className="mr-1 sm:mr-2 h-4 w-4" />
                Send to Board
              </Button>
              {isPlatformOwner && (
                <Button
                  onClick={async () => {
                    try {
                      toast.info('Downloading presentation deck...');
                      const response = await api.get('/deck/download', { responseType: 'blob' });
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', 'APC_EstateFlow_Investor_Deck.pptx');
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      window.URL.revokeObjectURL(url);
                      toast.success('Investor deck downloaded!');
                    } catch (error) {
                      console.error('Error downloading deck:', error);
                      toast.error('Failed to download investor deck');
                    }
                  }}
                  size="sm"
                  variant="outline"
                  data-testid="download-deck-btn"
                  className="text-xs sm:text-sm border-amber-300 text-amber-700 hover:bg-amber-50"
                >
                  <Download className="mr-1 sm:mr-2 h-4 w-4" />
                  Investor Deck
                </Button>
              )}
              <Badge className="bg-blue-100 text-blue-800 text-xs sm:text-sm">HOA Administrator</Badge>
              <Button 
                onClick={() => navigate('/kb')} 
                variant="ghost" 
                size="sm"
                data-testid="hoa-admin-dashboard-help-btn"
                className="text-xs sm:text-sm"
              >
                <HelpCircle className="mr-1 sm:mr-2 h-4 w-4" />
                <span className="hidden xs:inline">Help</span>
              </Button>
              <Button onClick={handleLogout} variant="outline" size="sm" className="text-xs sm:text-sm">
                <LogOut className="mr-1 sm:mr-2 h-4 w-4" />
                <span className="hidden xs:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Quick Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-6 mb-6 sm:mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-3 sm:p-6 sm:pb-2">
              <CardTitle className="text-xs sm:text-sm font-medium">Total Buildings</CardTitle>
              <Building2 className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
              <div className="text-lg sm:text-2xl font-bold">{dashboardData?.totals?.buildings || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-3 sm:p-6 sm:pb-2">
              <CardTitle className="text-xs sm:text-sm font-medium">Total Units</CardTitle>
              <Building2 className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
              <div className="text-lg sm:text-2xl font-bold">{dashboardData?.totals?.units || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-3 sm:p-6 sm:pb-2">
              <CardTitle className="text-xs sm:text-sm font-medium">Active Owners</CardTitle>
              <Users className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
              <div className="text-lg sm:text-2xl font-bold">{dashboardData?.totals?.owners_active || 0}</div>
              <p className="text-xs text-muted-foreground hidden sm:block">Homeowners</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-3 sm:p-6 sm:pb-2">
              <CardTitle className="text-xs sm:text-sm font-medium">Monthly Revenue</CardTitle>
              <DollarSign className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
              <div className="text-lg sm:text-2xl font-bold">${(dashboardData?.revenue_month || 0).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
              <p className="text-xs text-muted-foreground hidden sm:block">HOA Dues Collected</p>
            </CardContent>
          </Card>

          <Card className="col-span-2 sm:col-span-1">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-3 sm:p-6 sm:pb-2">
              <CardTitle className="text-xs sm:text-sm font-medium">Pending Requests</CardTitle>
              <Wrench className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
              <div className="text-lg sm:text-2xl font-bold">{dashboardData?.pending_maintenance || 0}</div>
              <p className="text-xs text-muted-foreground hidden sm:block">Maintenance</p>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 mb-6 sm:mb-8">
          {can('owners.manage') && <Button 
            className="bg-emerald-600 hover:bg-emerald-700 text-xs sm:text-sm py-2 sm:py-3"
            onClick={() => setShowAddOwner(true)}
          >
            <UserPlus className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" />
            <span className="hidden xs:inline">Add </span>Owner
          </Button>}
          {can('dues.manage') && <Button 
            className="bg-blue-600 hover:bg-blue-700 text-xs sm:text-sm py-2 sm:py-3"
            onClick={() => setShowRecordDues(true)}
          >
            <DollarSign className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" />
            <span className="hidden sm:inline">Record </span>Dues
          </Button>}
          {can('announcements.manage') && <Button 
            className="bg-purple-600 hover:bg-purple-700 text-xs sm:text-sm py-2 sm:py-3"
            onClick={() => setShowAnnouncement(true)}
          >
            <Bell className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" />
            <span className="hidden sm:inline">Create </span>Announce
          </Button>}
          {can('maintenance.manage') && <Button 
            className="bg-orange-600 hover:bg-orange-700 text-xs sm:text-sm py-2 sm:py-3"
            onClick={() => setShowMaintenance(true)}
          >
            <Wrench className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" />
            Maintain
          </Button>}
        </div>

        {/* Add Owner Dialog */}
        {showAddOwner && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="text-lg sm:text-xl">Add New Homeowner</CardTitle>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 pt-0">
                {!invitationCode ? (
                  <form onSubmit={handleAddOwner} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">First Name</label>
                        <input type="text" name="first_name" required className="w-full px-3 py-2 border rounded text-base" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Last Name</label>
                        <input type="text" name="last_name" required className="w-full px-3 py-2 border rounded text-base" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Email</label>
                      <input type="email" name="email" required className="w-full px-3 py-2 border rounded text-base" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Phone (E.164 format)</label>
                      <input type="tel" name="phone" required className="w-full px-3 py-2 border rounded text-base" placeholder="+12425551234" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Unit Number</label>
                      <input type="text" name="unit_number" required className="w-full px-3 py-2 border rounded text-base" placeholder="e.g., 101" />
                      <p className="text-xs text-gray-500 mt-1">This will create a unit if it doesn't exist</p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Button type="submit" className="flex-1 order-1 sm:order-none">Generate Invitation Code</Button>
                      <Button type="button" variant="outline" onClick={() => setShowAddOwner(false)} className="order-2 sm:order-none">Cancel</Button>
                    </div>
                  </form>
                ) : (
                  <div className="space-y-4">
                    <div className="bg-emerald-50 border-2 border-emerald-200 rounded-lg p-6 text-center">
                      <p className="text-sm text-gray-600 mb-2">Homeowner Invitation Code</p>
                      <p className="text-3xl font-bold text-emerald-600 tracking-wider mb-4">{invitationCode}</p>
                      <Button 
                        onClick={() => {
                          const textarea = document.createElement('textarea');
                          textarea.value = invitationCode;
                          textarea.style.position = 'fixed';
                          textarea.style.opacity = '0';
                          document.body.appendChild(textarea);
                          textarea.select();
                          try {
                            document.execCommand('copy');
                            toast.success('Code copied to clipboard!');
                          } catch (err) {
                            toast.error('Please copy manually: ' + invitationCode);
                          }
                          document.body.removeChild(textarea);
                        }}
                        variant="outline"
                        className="w-full"
                      >
                        Copy Code
                      </Button>
                    </div>
                    <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm text-emerald-800 mb-3">
                      <p className="font-medium">Invitation Email: Sent</p>
                      <p className="text-xs mt-1">The homeowner will receive an email with the code and a registration link.</p>
                    </div>
                    <div className="bg-gray-50 border border-gray-200 rounded p-3 text-sm text-gray-700">
                      <p className="font-medium mb-1">Manual Backup:</p>
                      <ol className="list-decimal list-inside space-y-1 text-xs">
                        <li>Copy the code above if the email doesn't arrive</li>
                        <li>Direct the homeowner to the HOA owner registration page</li>
                        <li>They'll use this code to complete registration</li>
                      </ol>
                    </div>
                    <Button 
                      onClick={() => {
                        setInvitationCode('');
                        setShowAddOwner(false);
                      }}
                      className="w-full"
                    >
                      Done
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Record HOA Dues Dialog */}
        {showRecordDues && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <Card className="w-full max-w-md">
              <CardHeader>
                <CardTitle>Record HOA Dues Payment</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={(e) => {
                  e.preventDefault();
                  toast.success('Payment recorded successfully!');
                  setShowRecordDues(false);
                }} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Homeowner Name</label>
                    <input type="text" required className="w-full px-3 py-2 border rounded" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Unit Number</label>
                    <input type="text" required className="w-full px-3 py-2 border rounded" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Amount ($)</label>
                    <input type="number" required className="w-full px-3 py-2 border rounded" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Payment Date</label>
                    <input type="date" required className="w-full px-3 py-2 border rounded" />
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" className="flex-1">Record Payment</Button>
                    <Button type="button" variant="outline" onClick={() => setShowRecordDues(false)}>Cancel</Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Create Announcement Dialog */}
        {showAnnouncement && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <Card className="w-full max-w-md">
              <CardHeader>
                <CardTitle>Create Community Announcement</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={(e) => {
                  e.preventDefault();
                  toast.success('Announcement sent to all homeowners!');
                  setShowAnnouncement(false);
                }} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Subject</label>
                    <input type="text" required className="w-full px-3 py-2 border rounded" placeholder="e.g., Pool Maintenance Schedule" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Message</label>
                    <textarea required className="w-full px-3 py-2 border rounded" rows="4" placeholder="Enter your announcement..."></textarea>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Priority</label>
                    <select className="w-full px-3 py-2 border rounded">
                      <option value="normal">Normal</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" className="flex-1">Send Announcement</Button>
                    <Button type="button" variant="outline" onClick={() => setShowAnnouncement(false)}>Cancel</Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Schedule Maintenance Dialog */}
        {showMaintenance && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <Card className="w-full max-w-md">
              <CardHeader>
                <CardTitle>Schedule Maintenance</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={(e) => {
                  e.preventDefault();
                  toast.success('Maintenance scheduled successfully!');
                  setShowMaintenance(false);
                }} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Maintenance Type</label>
                    <select required className="w-full px-3 py-2 border rounded">
                      <option value="">Select type</option>
                      <option value="plumbing">Plumbing</option>
                      <option value="electrical">Electrical</option>
                      <option value="hvac">HVAC</option>
                      <option value="landscaping">Landscaping</option>
                      <option value="general">General Repair</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Description</label>
                    <textarea required className="w-full px-3 py-2 border rounded" rows="3"></textarea>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Scheduled Date</label>
                    <input type="date" required className="w-full px-3 py-2 border rounded" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Assign Vendor</label>
                    <select className="w-full px-3 py-2 border rounded" name="vendor_id">
                      <option value="">Select a vendor (optional)</option>
                      {vendors.map(v => (
                        <option key={v.id} value={v.id}>{v.company_name}{v.contact_person ? ` — ${v.contact_person}` : ''}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" className="flex-1">Schedule</Button>
                    <Button type="button" variant="outline" onClick={() => setShowMaintenance(false)}>Cancel</Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 mb-6">
            <TabsList className="inline-flex w-max sm:grid sm:grid-cols-10 sm:w-full min-w-full sm:min-w-0 gap-1">
              <TabsTrigger value="overview" className="text-xs sm:text-sm px-2 sm:px-3 whitespace-nowrap">Overview</TabsTrigger>
              {can('meetings.view') && <TabsTrigger value="meetings" className="text-xs sm:text-sm px-2 sm:px-3 whitespace-nowrap text-blue-600" data-testid="meetings-tab">Meetings</TabsTrigger>}
              {can('owners.view') && <TabsTrigger value="owners" className="text-xs sm:text-sm px-2 sm:px-3 whitespace-nowrap">Owners</TabsTrigger>}
              {can('maintenance.view') && <TabsTrigger value="maintenance" className="text-xs sm:text-sm px-2 sm:px-3 whitespace-nowrap">Maintenance</TabsTrigger>}
              {can('vendors.view') && <TabsTrigger value="vendors" className="text-xs sm:text-sm px-2 sm:px-3 whitespace-nowrap">Vendors</TabsTrigger>}
              {can('financial.view') && <TabsTrigger value="assessments" className="text-xs sm:text-sm px-2 sm:px-3 whitespace-nowrap text-amber-600" data-testid="assessments-tab">Assessments</TabsTrigger>}
              {can('financial.view') && <TabsTrigger value="financial" className="text-xs sm:text-sm px-2 sm:px-3 whitespace-nowrap">Financial</TabsTrigger>}
              {can('analytics.view') && <TabsTrigger value="analytics" className="text-xs sm:text-sm px-2 sm:px-3 whitespace-nowrap text-indigo-600">Analytics</TabsTrigger>}
              {can('tax.view') && <TabsTrigger value="tax-pl" className="text-xs sm:text-sm px-2 sm:px-3 whitespace-nowrap text-purple-600">Tax & P&L</TabsTrigger>}
              {can('settings.manage') && <TabsTrigger value="settings" className="text-xs sm:text-sm px-2 sm:px-3 whitespace-nowrap">Settings</TabsTrigger>}
              {can('staff.view') && <TabsTrigger value="staff" className="text-xs sm:text-sm px-2 sm:px-3 whitespace-nowrap text-red-600" data-testid="staff-tab">Staff</TabsTrigger>}
            </TabsList>
          </div>

          <TabsContent value="overview">
            <div className="grid gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Recent Maintenance Requests</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {maintenanceRequests.slice(0, 5).map((request) => (
                      <div key={request.id} className="flex items-center justify-between p-4 border rounded-lg">
                        <div className="flex-1">
                          <h4 className="font-medium">{request.title}</h4>
                          <p className="text-sm text-gray-600">{request.category} - {request.priority}</p>
                          <p className="text-xs text-gray-500">{new Date(request.created_at).toLocaleDateString()}</p>
                        </div>
                        <Badge className={getStatusColor(request.status)}>
                          {request.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Meetings / AGM Tab */}
          <TabsContent value="meetings">
            <HOAMeetings orgId={dashboardData?.organization?.id || 'demo-hoa'} owners={owners} />
          </TabsContent>

          <TabsContent value="owners">
            <Card>
              <CardHeader className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                  <CardTitle className="text-lg sm:text-xl">Homeowner Management</CardTitle>
                  <Button className="bg-emerald-600 hover:bg-emerald-700 text-sm" onClick={() => setShowAddOwner(true)}>
                    <UserPlus className="mr-2 h-4 w-4" />
                    Add New Owner
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0 sm:p-6 sm:pt-0">
                {/* Mobile Card View */}
                <div className="block sm:hidden space-y-3 p-4">
                  {owners.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">No owners found</div>
                  ) : (
                    owners.map((owner) => {
                      const paymentStatus = getOwnerPaymentStatus(owner.id);
                      return (
                        <div key={owner.id} className="border rounded-lg p-4 bg-white">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="font-medium">{owner.first_name} {owner.last_name}</p>
                              <p className="text-sm text-gray-500">Unit {owner.unit_number || 'N/A'}</p>
                            </div>
                            <Badge className={paymentStatus.color}>{paymentStatus.status}</Badge>
                          </div>
                          <div className="text-sm text-gray-600 space-y-1">
                            <p>{owner.email}</p>
                            <p>{owner.phone || 'No phone'}</p>
                            {paymentStatus.amount && (
                              <p className="font-medium">Dues: ${paymentStatus.amount.toFixed(2)}</p>
                            )}
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="w-full mt-3"
                            onClick={() => setExpandedOwner(expandedOwner === owner.id ? null : owner.id)}
                          >
                            <Receipt className="h-3 w-3 mr-1" />
                            View History
                          </Button>
                        </div>
                      );
                    })
                  )}
                </div>
                
                {/* Desktop Table View */}
                <div className="hidden sm:block overflow-x-auto">
                  <table className="w-full min-w-[800px]">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left p-3 text-sm font-medium text-gray-700">Owner Name</th>
                        <th className="text-left p-3 text-sm font-medium text-gray-700">Unit</th>
                        <th className="text-left p-3 text-sm font-medium text-gray-700">Email</th>
                        <th className="text-left p-3 text-sm font-medium text-gray-700">Phone</th>
                        <th className="text-left p-3 text-sm font-medium text-gray-700">Current Dues</th>
                        <th className="text-left p-3 text-sm font-medium text-gray-700">Payment Status</th>
                        <th className="text-left p-3 text-sm font-medium text-gray-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {owners.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="text-center py-8 text-gray-500">
                            No owners found
                          </td>
                        </tr>
                      ) : (
                        owners.map((owner) => {
                          const paymentStatus = getOwnerPaymentStatus(owner.id);
                          const isExpanded = expandedOwner === owner.id;
                          const paymentHistory = getOwnerPaymentHistory(owner.id);
                          const totals = calculateOwnerTotals(owner.id);
                          
                          return (
                            <React.Fragment key={owner.id}>
                              <tr className="border-t hover:bg-gray-50">
                                <td className="p-3">
                                  <div className="flex items-center gap-2">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => setExpandedOwner(isExpanded ? null : owner.id)}
                                      className="p-1"
                                    >
                                      {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                    </Button>
                                    <div>
                                      <div className="font-medium">{owner.first_name} {owner.last_name}</div>
                                      {paymentHistory.length > 0 && (
                                        <div className="text-xs text-gray-500">{paymentHistory.length} payment records</div>
                                      )}
                                    </div>
                                  </div>
                                </td>
                                <td className="p-3">
                                  <span className="font-medium">{owner.unit_number || 'N/A'}</span>
                                </td>
                                <td className="p-3 text-sm text-gray-600">{owner.email}</td>
                                <td className="p-3 text-sm text-gray-600">{owner.phone || 'N/A'}</td>
                                <td className="p-3">
                                  {paymentStatus.amount ? (
                                    <span className="font-medium">${paymentStatus.amount.toFixed(2)}</span>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </td>
                                <td className="p-3">
                                  <Badge className={paymentStatus.color}>
                                    {paymentStatus.status}
                                  </Badge>
                                </td>
                                <td className="p-3">
                                  <div className="flex gap-2">
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      onClick={() => setExpandedOwner(isExpanded ? null : owner.id)}
                                    >
                                      <Receipt className="h-3 w-3 mr-1" />
                                      History
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                              
                              {/* Expanded Payment History Row */}
                              {isExpanded && (
                                <tr className="bg-blue-50">
                                  <td colSpan="7" className="p-4">
                                    <div className="space-y-4">
                                      <div className="flex justify-between items-center">
                                        <h4 className="font-semibold text-gray-900">Payment History - {owner.first_name} {owner.last_name}</h4>
                                        <div className="flex gap-4 text-sm">
                                          <span className="text-green-600 font-medium">Paid: ${totals.totalPaid.toFixed(2)}</span>
                                          <span className="text-red-600 font-medium">Unpaid: ${totals.totalUnpaid.toFixed(2)}</span>
                                        </div>
                                      </div>
                                      
                                      {paymentHistory.length === 0 ? (
                                        <p className="text-gray-500 text-sm">No payment history available</p>
                                      ) : (
                                        <div className="bg-white rounded-lg border">
                                          <table className="w-full text-sm">
                                            <thead className="bg-gray-50">
                                              <tr>
                                                <th className="text-left p-3 font-medium text-gray-700">Due Date</th>
                                                <th className="text-left p-3 font-medium text-gray-700">Paid Date</th>
                                                <th className="text-left p-3 font-medium text-gray-700">Amount</th>
                                                <th className="text-left p-3 font-medium text-gray-700">Late Fee</th>
                                                <th className="text-left p-3 font-medium text-gray-700">Total</th>
                                                <th className="text-left p-3 font-medium text-gray-700">Payment Method</th>
                                                <th className="text-left p-3 font-medium text-gray-700">Status</th>
                                                <th className="text-left p-3 font-medium text-gray-700">Transaction ID</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {paymentHistory.map((record, idx) => (
                                                <tr key={idx} className="border-t hover:bg-gray-50">
                                                  <td className="p-3">
                                                    {new Date(record.due_date).toLocaleDateString('en-US', { 
                                                      year: 'numeric', 
                                                      month: 'short', 
                                                      day: 'numeric' 
                                                    })}
                                                  </td>
                                                  <td className="p-3">
                                                    {record.paid_date ? (
                                                      <span className="text-green-600 font-medium">
                                                        {new Date(record.paid_date).toLocaleDateString('en-US', { 
                                                          year: 'numeric', 
                                                          month: 'short', 
                                                          day: 'numeric' 
                                                        })}
                                                      </span>
                                                    ) : (
                                                      <span className="text-gray-400">Not paid</span>
                                                    )}
                                                  </td>
                                                  <td className="p-3 font-medium">${record.amount.toFixed(2)}</td>
                                                  <td className="p-3">
                                                    {record.late_fee > 0 ? (
                                                      <span className="text-orange-600">${record.late_fee.toFixed(2)}</span>
                                                    ) : (
                                                      <span className="text-gray-400">-</span>
                                                    )}
                                                  </td>
                                                  <td className="p-3 font-bold">
                                                    ${(record.amount + (record.late_fee || 0)).toFixed(2)}
                                                  </td>
                                                  <td className="p-3 capitalize">
                                                    {record.payment_method || '-'}
                                                  </td>
                                                  <td className="p-3">
                                                    <Badge className={
                                                      record.status === 'paid' ? 'bg-green-100 text-green-800' :
                                                      record.status === 'unpaid' ? 'bg-red-100 text-red-800' :
                                                      record.status === 'late' ? 'bg-orange-100 text-orange-800' :
                                                      'bg-gray-100 text-gray-800'
                                                    }>
                                                      {record.status}
                                                    </Badge>
                                                  </td>
                                                  <td className="p-3 text-xs text-gray-500">
                                                    {record.transaction_id || 'N/A'}
                                                  </td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      )}
                                      
                                      {/* Notes section */}
                                      {paymentHistory.length > 0 && (
                                        <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-sm">
                                          <strong>Accounting Notes:</strong>
                                          <ul className="mt-2 space-y-1 ml-4 list-disc text-gray-700">
                                            <li>Total Paid: ${totals.totalPaid.toFixed(2)} ({totals.paidCount} payments)</li>
                                            <li>Total Outstanding: ${totals.totalUnpaid.toFixed(2)} ({totals.unpaidCount} unpaid)</li>
                                            <li>Payment History: {totals.recordCount} months recorded</li>
                                          </ul>
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {owners.length > 0 && (
                  <div className="mt-4 p-3 sm:p-4 bg-blue-50 rounded-lg mx-4 sm:mx-0 mb-4 sm:mb-0">
                    <h4 className="font-medium text-blue-900 mb-2 text-sm sm:text-base">Payment Summary</h4>
                    <div className="grid grid-cols-3 gap-2 sm:gap-4 text-xs sm:text-sm">
                      <div>
                        <span className="text-blue-600">Total Owners:</span>
                        <span className="ml-1 sm:ml-2 font-bold">{owners.length}</span>
                      </div>
                      <div>
                        <span className="text-green-600">Paid:</span>
                        <span className="ml-1 sm:ml-2 font-bold">
                          {owners.filter(o => getOwnerPaymentStatus(o.id).status === 'Paid').length}
                        </span>
                      </div>
                      <div>
                        <span className="text-red-600">Unpaid:</span>
                        <span className="ml-1 sm:ml-2 font-bold">
                          {owners.filter(o => getOwnerPaymentStatus(o.id).status === 'Unpaid').length}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="maintenance">
            <Card>
              <CardHeader className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                  <CardTitle className="text-lg sm:text-xl">Maintenance Requests</CardTitle>
                  <Button 
                    className="bg-emerald-600 hover:bg-emerald-700 text-sm" 
                    size="sm"
                    onClick={() => setShowMaintenance(true)}
                  >
                    <PlusCircle className="mr-2 h-4 w-4" />
                    Schedule Maintenance
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 pt-0">
                <div className="space-y-3 sm:space-y-4">
                  {maintenanceRequests.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      No maintenance requests yet
                    </div>
                  ) : (
                    maintenanceRequests.map((request) => (
                      <div key={request.id} className="p-3 sm:p-4 border rounded-lg">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-2">
                          <div className="flex-1">
                            <h4 className="font-medium text-sm sm:text-base">{request.title}</h4>
                            <p className="text-xs sm:text-sm text-gray-600">{request.description}</p>
                          </div>
                          <Badge className={getStatusColor(request.status)}>
                            {request.status}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500 mt-2">
                          <span>Category: <span className="font-medium capitalize">{request.category}</span></span>
                          <span>Priority: <span className={`font-medium capitalize ${
                            request.priority === 'urgent' ? 'text-red-600' :
                            request.priority === 'high' ? 'text-orange-600' : 'text-gray-600'
                          }`}>{request.priority}</span></span>
                          <span>{new Date(request.created_at).toLocaleDateString()}</span>
                        </div>
                        {request.scheduled_date && (
                          <p className="text-xs sm:text-sm text-gray-600 mt-2">
                            Scheduled: {new Date(request.scheduled_date).toLocaleDateString()}
                          </p>
                        )}
                        {request.estimated_cost > 0 && (
                          <p className="text-xs sm:text-sm text-gray-600">
                            Estimated Cost: ${request.estimated_cost.toFixed(2)}
                          </p>
                        )}
                        <div className="flex flex-wrap gap-2 mt-3">
                          {request.status === 'pending' && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-xs sm:text-sm"
                              onClick={() => handleMaintenanceStatusUpdate(request.id, 'in_progress')}
                            >
                              Start Work
                            </Button>
                          )}
                          {request.status === 'in_progress' && (
                            <Button 
                              size="sm" 
                              className="bg-green-600 hover:bg-green-700 text-xs sm:text-sm"
                              onClick={() => handleMaintenanceStatusUpdate(request.id, 'completed')}
                            >
                              <CheckCircle className="mr-1 h-3 w-3" />
                              Complete
                            </Button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="vendors">
            <VendorManager apiPrefix="/hoa" />
          </TabsContent>

          {/* Assessments Tab */}
          <TabsContent value="assessments">
            <HOAAssessments orgId={dashboardData?.organization?.id || 'demo-hoa'} />
          </TabsContent>

          <TabsContent value="financial">
            <div className="space-y-4 sm:space-y-6">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                <h2 className="text-lg sm:text-2xl font-bold">HOA Financial Summary</h2>
                {can('dues.manage') && <Button onClick={() => setShowRecordDues(true)} className="text-sm">
                  <PlusCircle className="mr-2 h-4 w-4" />
                  Record Dues
                </Button>}
              </div>

              {/* Financial Snapshot Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
                <Card className="border-l-4 border-l-green-500">
                  <CardHeader className="pb-2 p-3 sm:p-6 sm:pb-2">
                    <CardTitle className="text-xs sm:text-sm font-medium text-gray-600">Dues Collected</CardTitle>
                  </CardHeader>
                  <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
                    <div className="text-lg sm:text-3xl font-bold text-green-600">
                      ${(financialSummary?.income?.total || 0).toLocaleString('en-US', {minimumFractionDigits: 2})}
                    </div>
                    <p className="text-xs text-green-600 mt-1 flex items-center">
                      <TrendingUp className="h-3 w-3 mr-1" />
                      <span className="hidden sm:inline">{financialSummary?.details?.dues_records || 0} payments</span>
                      <span className="sm:hidden">{financialSummary?.details?.dues_records || 0}</span>
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-red-500">
                  <CardHeader className="pb-2 p-3 sm:p-6 sm:pb-2">
                    <CardTitle className="text-xs sm:text-sm font-medium text-gray-600">Outstanding</CardTitle>
                  </CardHeader>
                  <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
                    <div className="text-lg sm:text-3xl font-bold text-red-600">
                      ${(financialSummary?.outstanding_dues || 0).toLocaleString('en-US', {minimumFractionDigits: 2})}
                    </div>
                    <p className="text-xs text-red-600 mt-1">
                      {financialSummary?.overdue_owners || 0} overdue
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-orange-500">
                  <CardHeader className="pb-2 p-3 sm:p-6 sm:pb-2">
                    <CardTitle className="text-xs sm:text-sm font-medium text-gray-600">Expenses</CardTitle>
                  </CardHeader>
                  <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
                    <div className="text-lg sm:text-3xl font-bold text-orange-600">
                      ${(financialSummary?.expenses?.total || 0).toLocaleString('en-US', {minimumFractionDigits: 2})}
                    </div>
                    <p className="text-xs text-gray-600 mt-1 hidden sm:block">
                      Maintenance & utilities
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-blue-500">
                  <CardHeader className="pb-2 p-3 sm:p-6 sm:pb-2">
                    <CardTitle className="text-xs sm:text-sm font-medium text-gray-600">Net Income</CardTitle>
                  </CardHeader>
                  <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
                    <div className={`text-lg sm:text-3xl font-bold ${(financialSummary?.net_income || 0) >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                      ${(financialSummary?.net_income || 0).toLocaleString('en-US', {minimumFractionDigits: 2})}
                    </div>
                    <p className="text-xs text-gray-600 mt-1">
                      After expenses
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Collection by Building/Unit */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Dues Collection by Building</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {(financialSummary?.buildings || []).length > 0 ? (
                        financialSummary.buildings.map((bldg, idx) => (
                          <div key={idx}>
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium">{bldg.name}</p>
                                <p className="text-xs text-gray-500">{bldg.units} units</p>
                              </div>
                              <div className="text-right">
                                <p className={`font-bold ${bldg.collection_rate >= 90 ? 'text-green-600' : 'text-orange-600'}`}>
                                  ${(bldg.collected || 0).toLocaleString('en-US', {minimumFractionDigits: 2})}
                                </p>
                                <p className="text-xs text-gray-500">{bldg.collection_rate}% collected</p>
                              </div>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                              <div className={`h-2 rounded-full ${bldg.collection_rate >= 90 ? 'bg-green-500' : 'bg-orange-500'}`} style={{width: `${bldg.collection_rate}%`}}></div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-gray-500 text-sm">No building data available</p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Recent HOA Transactions</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3" data-testid="recent-hoa-transactions">
                      {(financialSummary?.recent_transactions || []).length > 0 ? (
                        financialSummary.recent_transactions.map((txn, idx) => (
                          <div key={idx} className={`flex items-center justify-between py-2 ${idx < financialSummary.recent_transactions.length - 1 ? 'border-b' : ''}`}>
                            <div>
                              <p className="font-medium text-sm">{txn.description}</p>
                              <p className="text-xs text-gray-500">{txn.date}</p>
                            </div>
                            <Badge className={txn.type === 'income' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                              {txn.type === 'income' ? '+' : '-'}${(txn.amount || 0).toLocaleString('en-US', {minimumFractionDigits: 2})}
                            </Badge>
                          </div>
                        ))
                      ) : (
                        <p className="text-gray-500 text-sm">No recent transactions</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Summary Stats */}
              <Card className="bg-gradient-to-r from-teal-50 to-emerald-50">
                <CardContent className="pt-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center" data-testid="financial-unit-stats">
                    <div>
                      <p className="text-2xl font-bold text-teal-700">{financialSummary?.unit_stats?.total_units || dashboardData?.totals?.units || 0}</p>
                      <p className="text-sm text-gray-600">Total Units</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-green-700">{financialSummary?.unit_stats?.dues_paid || 0}</p>
                      <p className="text-sm text-gray-600">Dues Paid</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-red-700">{financialSummary?.unit_stats?.dues_pending || 0}</p>
                      <p className="text-sm text-gray-600">Dues Pending</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-blue-700">{financialSummary?.unit_stats?.collection_rate || 0}%</p>
                      <p className="text-sm text-gray-600">Collection Rate</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Vendor Invoices Review Queue */}
              <VendorInvoiceReview />
            </div>
          </TabsContent>

          <TabsContent value="analytics">
            <FinancialAnalytics 
              orgId={dashboardData?.organization?.id} 
              orgName={dashboardData?.organization?.name || "HOA"}
              orgType="hoa"
            />
          </TabsContent>

          <TabsContent value="tax-pl">
            <PLReports 
              orgId={dashboardData?.organization?.id} 
              orgName={dashboardData?.organization?.name || "HOA"}
              orgCountry={dashboardData?.organization?.country || "US"}
            />
          </TabsContent>

          <TabsContent value="settings">
            <Card>
              <CardHeader>
                <CardTitle>HOA Settings & Configuration</CardTitle>
              </CardHeader>
              <CardContent>
                {settings ? (
                  <form onSubmit={handleUpdateSettings} className="space-y-6">
                    <div className="grid grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label>Default Monthly Dues ($)</Label>
                        <Input 
                          name="default_dues"
                          type="number"
                          step="0.01"
                          defaultValue={settings.default_dues || 350}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Late Fee Rate (%)</Label>
                        <Input 
                          name="late_fee_rate"
                          type="number"
                          step="0.01"
                          defaultValue={settings.late_fee_rate || 5.0}
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label>Maintenance Fee Threshold ($)</Label>
                        <Input 
                          name="maintenance_threshold"
                          type="number"
                          step="0.01"
                          defaultValue={settings.maintenance_threshold || 500}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Due Day of Month</Label>
                        <Input 
                          name="due_day"
                          type="number"
                          min="1"
                          max="31"
                          defaultValue={settings.due_day || 1}
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Grace Period (Days)</Label>
                      <Input 
                        name="grace_period_days"
                        type="number"
                        min="0"
                        defaultValue={settings.grace_period_days || 5}
                      />
                    </div>

                    <div className="space-y-4">
                      <Label className="text-base font-semibold">Notification Preferences</Label>
                      <div className="flex items-center space-x-2">
                        <input 
                          type="checkbox"
                          name="notify_email"
                          id="notify_email"
                          defaultChecked={settings.notify_email !== false}
                          className="rounded"
                        />
                        <Label htmlFor="notify_email" className="font-normal cursor-pointer">
                          Enable Email Notifications
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input 
                          type="checkbox"
                          name="notify_sms"
                          id="notify_sms"
                          defaultChecked={settings.notify_sms === true}
                          className="rounded"
                        />
                        <Label htmlFor="notify_sms" className="font-normal cursor-pointer">
                          Enable SMS Notifications
                        </Label>
                      </div>
                    </div>

                    <div className="pt-4">
                      <Button type="submit">
                        <Settings className="mr-2 h-4 w-4" />
                        Save Settings
                      </Button>
                    </div>
                  </form>
                ) : (
                  <div>Loading settings...</div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="staff">
            <StaffManagement />
          </TabsContent>
        </Tabs>
      </main>

      {/* Modals */}
      <ScheduleMaintenanceModal 
        open={showMaintenance}
        onClose={() => setShowMaintenance(false)}
        onSuccess={() => {
          fetchMaintenanceRequests();
          fetchDashboardData();
        }}
      />

      <RecordDuesModal 
        open={showRecordDues}
        onClose={() => setShowRecordDues(false)}
        onSuccess={() => {
          fetchFinancialSummary();
          fetchDashboardData();
        }}
      />

      <BoardReportEmailModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        orgName={dashboardData?.organization?.name || 'HOA'}
      />
    </div>
  );
};

export default HOAAdminDashboard;
